import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import MedicationInput from './components/MedicationInput';
import Options from '../Options';
import useTransition from '../../hooks/useTransition';
import useIsPolaris from '../../hooks/useIsPolaris';
import useInterview from 'src/interviews/hooks/useInterview';
import useInterviewType from 'src/interviews/hooks/useInterviewType';
import useFormatMedications from './hooks/useFormatMedications';
import LoadingContainer from '../LoadingContainer';
import {
  trackMedicationInputViewed,
  trackMedicationInputAnswered,
  trackMedicationInputNotFound,
  trackMedicationInputSkipped,
  trackMedicationConfirmationQuestionAnswered,
  trackMedicationConfirmationQuestionViewed,
} from './analytics';

import type {
  IPolarisInterviewOption,
  IPolarisInterviewQuestion,
} from 'src/packages/polaris/types/interviews';

const STEP_2_QUESTION_TEXT =
  'We’ve matched your medication list with what we have in our system. ' +
  'Select all the medications you would like to proceed with.';

const MedicationQuestion = ({
  question,
  onQuestionSubmit,
  setParsedQuestionText,
}: {
  question: IPolarisInterviewQuestion;
  onQuestionSubmit?: (questionIndex: number) => void;
  setParsedQuestionText: (text: string) => void;
}) => {
  const transition = useTransition();
  const isPolaris = useIsPolaris();
  const { token } = useInterview();
  const interviewType = useInterviewType();

  const [medications, setMedications] = useState<string[]>([]);
  const [step, setStep] = useState<number>(1);
  const [value, setValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const previousValueRef = useRef<string | null>(null);

  const { mutateAsync: formatMedications } = useFormatMedications();

  const analyticsProps = useMemo(
    () => ({
      interviewToken: token || '',
      interviewType,
      medications: medications,
    }),
    [medications, interviewType, token],
  );

  useEffect(() => {
    if (step === 1) {
      trackMedicationInputViewed(analyticsProps);
    }
  }, [step, analyticsProps]);

  useEffect(() => {
    if (step === 2) {
      trackMedicationConfirmationQuestionViewed(analyticsProps);
    }
  }, [step, analyticsProps]);

  useEffect(() => {
    if (medications.length > 0) {
      trackMedicationInputAnswered(analyticsProps);
    }
  }, [medications, analyticsProps]);

  const handleSubmit = useCallback(async () => {
    if (!value.trim()) {
      return;
    } else if (previousValueRef.current === value) {
      if (isError) {
        handleSkip();
      } else {
        setStep(2);
        setParsedQuestionText(STEP_2_QUESTION_TEXT);
      }
    } else {
      setIsLoading(true);
      previousValueRef.current = value;
      try {
        const formattedMedications = await formatMedications({ value });

        const formattedOptions: IPolarisInterviewOption[] = [
          {
            text: 'None of the above',
            token: uuid(),
            exclusive: true,
          },
          ...formattedMedications.map((medication) => ({
            text: medication,
            token: uuid(),
            exclusive: false,
          })),
        ];

        setMedications(formattedMedications);
        question['options'] = formattedOptions;
        question['answer'] = formattedOptions.filter((option) => !option.exclusive);
        setStep(2);
        setParsedQuestionText(STEP_2_QUESTION_TEXT);
        setIsLoading(false);
      } catch (error) {
        console.error('Error formatting medications:', error);
        trackMedicationInputNotFound(analyticsProps);
        setIsLoading(false);
        setIsError(true);
      }
    }
  }, [value, formatMedications, question, analyticsProps, isError]);

  const handleSkip = useCallback(() => {
    if (question?.index && question.index >= 0) {
      onQuestionSubmit?.(question.index);
    }

    trackMedicationInputSkipped(analyticsProps);

    transition({
      data: {
        value: medications.join(', '),
        question,
        isPolaris,
      },
    });
  }, [question, transition, medications, isPolaris, onQuestionSubmit, analyticsProps]);

  const handleGoBack = () => {
    setStep(1);
    setParsedQuestionText(question?.text?.replace(/\[|\]/g, '') ?? null);
  };

  const handleQuestionSubmitInStep2 = useCallback(
    (questionIndex: number) => {
      if (onQuestionSubmit) {
        onQuestionSubmit(questionIndex);
      }

      trackMedicationConfirmationQuestionAnswered(analyticsProps);
    },
    [onQuestionSubmit, analyticsProps],
  );

  useEffect(() => {
    // clear error state when value changes
    setIsError(false);
  }, [value]);

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <>
      {step === 1 && (
        <div>
          <MedicationInput
            value={value}
            setValue={setValue}
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            isError={isError}
          />
        </div>
      )}
      {step === 2 && (
        <div>
          <Options
            onQuestionSubmit={handleQuestionSubmitInStep2}
            question={question}
            onGoBackClick={handleGoBack}
            isMultiSelect
            showSubmitButton
          />
        </div>
      )}
    </>
  );
};

export default MedicationQuestion;
